/**
 * Niet in het origineel: de HTTP-routes voor het inloggen van de spelleider (`/api/auth`)
 * en de pagina Spelleiders (`/api/spelleiders`). Het contract staat in
 * `shared/src/spelleider.ts`.
 *
 * Net als in `admin-api.ts` gooit elke functie bij een fout een `Error` met een tekst die op
 * het scherm kan. Hier is dat een `AuthFout`, met de HTTP-status erbij en bij een afgekeurd
 * wachtwoord de losse bezwaren in `fouten`.
 *
 * De sessiecookie gaat vanzelf mee: de API staat op dezelfde origin.
 */
import type {
  IkAntwoord,
  LinkInfo,
  OpenUitnodiging,
  Spelleider,
  SpelleidersOverzicht,
} from '@ame/shared';

/** Een fout van de server, met de status (`null` als de server niet te bereiken was). */
export class AuthFout extends Error {
  override name = 'AuthFout';
  constructor(
    message: string,
    readonly status: number | null,
    /** Bij een afgekeurd wachtwoord (400): alle bezwaren, om onder elkaar te tonen. */
    readonly fouten: string[] = [],
  ) {
    super(message);
  }
}

/** De tekst als de sessie onderweg verlopen is; `admin-api.ts` gebruikt dezelfde. */
export const SESSIE_VERLOPEN = 'Je sessie is verlopen. Log opnieuw in.';

const luisteraars = new Set<() => void>();

/**
 * Laat `luisteraar` weten wanneer een route van de spelleider 401 geeft: de sessie is dan
 * verlopen of beëindigd. `AlleenSpelleider` toont daarop weer het inlogscherm. Geeft een
 * functie terug die het luisteren stopt.
 */
export function opSessieVerlopen(luisteraar: () => void): () => void {
  luisteraars.add(luisteraar);
  return () => {
    luisteraars.delete(luisteraar);
  };
}

/** Voor `admin-api.ts` en de routes hieronder: de server kent deze sessie niet meer. */
export function meldSessieVerlopen(): void {
  for (const luisteraar of luisteraars) luisteraar();
}

// ---------------------------------------------------------------------------------------
// Inloggen, uitloggen en de links uit de mails
// ---------------------------------------------------------------------------------------

/** Wie er is ingelogd (`GET /api/auth/ik`), of `null`. */
export async function ikOphalen(): Promise<Spelleider | null> {
  const antwoord = await verzoek('/api/auth/ik');
  return ((await antwoord.json()) as IkAntwoord).spelleider;
}

/** `POST /api/auth/inloggen`. Een fout e-mailadres of wachtwoord geeft een `AuthFout` 401. */
export async function inloggen(email: string, wachtwoord: string): Promise<Spelleider> {
  return ingelogd(await verzoek('/api/auth/inloggen', post({ email, wachtwoord })));
}

/** `POST /api/auth/uitloggen`. Was de sessie al weg, dan is dat ook goed. */
export async function uitloggen(): Promise<void> {
  try {
    await verzoek('/api/auth/uitloggen', post());
  } catch (fout) {
    if (fout instanceof AuthFout && fout.status === 401) return;
    throw fout;
  }
}

/** `POST /api/auth/wachtwoord-vergeten`. Het antwoord zegt niet of het adres bestaat. */
export async function wachtwoordVergeten(email: string): Promise<void> {
  await verzoek('/api/auth/wachtwoord-vergeten', post({ email }));
}

/** Voor wie de herstellink is (`GET /api/auth/herstel/:token`). Verlopen: `AuthFout` 410. */
export async function herstelOphalen(token: string): Promise<LinkInfo> {
  const antwoord = await verzoek(`/api/auth/herstel/${encodeURIComponent(token)}`);
  return (await antwoord.json()) as LinkInfo;
}

/** Slaat het nieuwe wachtwoord op en logt in (`POST /api/auth/herstel/:token`). */
export async function wachtwoordHerstellen(token: string, wachtwoord: string): Promise<Spelleider> {
  return ingelogd(
    await verzoek(`/api/auth/herstel/${encodeURIComponent(token)}`, post({ wachtwoord })),
  );
}

/** Voor wie de uitnodiging is (`GET /api/auth/uitnodiging/:token`). Verlopen: `AuthFout` 410. */
export async function uitnodigingOphalen(token: string): Promise<LinkInfo> {
  const antwoord = await verzoek(`/api/auth/uitnodiging/${encodeURIComponent(token)}`);
  return (await antwoord.json()) as LinkInfo;
}

/** Maakt het account van de uitnodiging en logt in (`POST /api/auth/uitnodiging/:token`). */
export async function accountAanmaken(
  token: string,
  naam: string,
  wachtwoord: string,
): Promise<Spelleider> {
  return ingelogd(
    await verzoek(`/api/auth/uitnodiging/${encodeURIComponent(token)}`, post({ naam, wachtwoord })),
  );
}

// ---------------------------------------------------------------------------------------
// De pagina Spelleiders; alles hieronder vraagt een sessie
// ---------------------------------------------------------------------------------------

/** Wie er toegang heeft en welke uitnodigingen nog open staan (`GET /api/spelleiders`). */
export async function spelleidersOphalen(): Promise<SpelleidersOverzicht> {
  const antwoord = await verzoek('/api/spelleiders', undefined, true);
  return (await antwoord.json()) as SpelleidersOverzicht;
}

/** Stuurt een uitnodiging (`POST /api/spelleiders/uitnodigingen`). Heeft het adres al toegang: 409. */
export async function uitnodigen(email: string): Promise<OpenUitnodiging> {
  const antwoord = await verzoek('/api/spelleiders/uitnodigingen', post({ email }), true);
  return (await antwoord.json()) as OpenUitnodiging;
}

/** Stuurt de uitnodiging opnieuw, met een nieuwe link (`POST .../uitnodigingen/:id/opnieuw`). */
export async function uitnodigingOpnieuw(id: string): Promise<OpenUitnodiging> {
  const antwoord = await verzoek(
    `/api/spelleiders/uitnodigingen/${encodeURIComponent(id)}/opnieuw`,
    post(),
    true,
  );
  return (await antwoord.json()) as OpenUitnodiging;
}

/** Trekt een uitnodiging in (`DELETE /api/spelleiders/uitnodigingen/:id`). */
export async function uitnodigingIntrekken(id: string): Promise<void> {
  await verzoek(`/api/spelleiders/uitnodigingen/${encodeURIComponent(id)}`, verwijder(), true);
}

/** Trekt de toegang van een spelleider in (`DELETE /api/spelleiders/:id`). Jezelf: 409. */
export async function toegangIntrekken(id: string): Promise<void> {
  await verzoek(`/api/spelleiders/${encodeURIComponent(id)}`, verwijder(), true);
}

// ---------------------------------------------------------------------------------------

/** Een POST met JSON. Ook zonder inhoud gaat het type mee: de server eist dat. */
function post(inhoud?: unknown): RequestInit {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: inhoud === undefined ? undefined : JSON.stringify(inhoud),
  };
}

function verwijder(): RequestInit {
  return { method: 'DELETE', headers: { 'Content-Type': 'application/json' } };
}

/** De spelleider uit een `IkAntwoord` na inloggen, een nieuw wachtwoord of een nieuw account. */
async function ingelogd(antwoord: Response): Promise<Spelleider> {
  const { spelleider } = (await antwoord.json()) as IkAntwoord;
  if (!spelleider) throw new AuthFout('De server logde je niet in. Probeer het opnieuw.', null);
  return spelleider;
}

/**
 * `fetch` dat bij een foutstatus een `AuthFout` gooit met de `fout` van de server. Met
 * `sessieNodig` betekent een 401 dat de sessie weg is; dat gaat dan ook naar
 * `opSessieVerlopen`. Bij het inloggen zelf is een 401 gewoon een fout wachtwoord.
 */
async function verzoek(pad: string, init?: RequestInit, sessieNodig = false): Promise<Response> {
  let antwoord: Response;
  try {
    antwoord = await fetch(pad, init);
  } catch {
    throw new AuthFout('De server is niet bereikbaar. Controleer je verbinding.', null);
  }
  if (antwoord.ok) return antwoord;

  if (sessieNodig && antwoord.status === 401) {
    meldSessieVerlopen();
    throw new AuthFout(SESSIE_VERLOPEN, 401);
  }

  let melding = `De server gaf een fout (${antwoord.status}).`;
  let fouten: string[] = [];
  try {
    const inhoud: unknown = await antwoord.json();
    if (typeof inhoud === 'object' && inhoud !== null) {
      if ('fout' in inhoud && typeof inhoud.fout === 'string') melding = inhoud.fout;
      if ('fouten' in inhoud && Array.isArray(inhoud.fouten)) fouten = inhoud.fouten.map(String);
    }
  } catch {
    // Geen JSON; de algemene melding volstaat.
  }
  throw new AuthFout(melding, antwoord.status, fouten);
}
